import { useState, useEffect, useCallback } from 'react';
import { getDeviceType } from '../utils/responsive';

/**
 * Hook for responsive design - provides device type detection
 * Based on Sidebar.jsx pattern for consistency
 *
 * @returns {Object} Device state and info
 *   - isMobile: boolean (width < 768px)
 *   - isTablet: boolean (768px <= width < 1024px)
 *   - isDesktop: boolean (width >= 1024px)
 *   - width: number (current window width)
 *   - height: number (current window height)
 *   - breakpoint: string (xs, sm, md, lg, xl, 2xl)
 */
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState(() => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false,
        isDesktop: true,
        width: 1024,
        height: 768,
        breakpoint: 'lg',
      };
    }

    const deviceType = getDeviceType(window.innerWidth);
    return {
      isMobile: deviceType.isMobile,
      isTablet: deviceType.isTablet,
      isDesktop: deviceType.isDesktop,
      width: window.innerWidth,
      height: window.innerHeight,
      breakpoint: deviceType.type,
    };
  });

  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const deviceType = getDeviceType(width);

    setScreenSize({
      isMobile: deviceType.isMobile,
      isTablet: deviceType.isTablet,
      isDesktop: deviceType.isDesktop,
      width,
      height,
      breakpoint: deviceType.type,
    });
  }, []);

  useEffect(() => {
    // Call immediately to ensure state is updated
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return screenSize;
};

export default useResponsive;
